import React, { useCallback, useEffect, useRef } from 'react';
import EditComponent from '../EditComponent';
import { propsSignature } from '~/lib/propsSignature';

type Component = {
  _id?: string;
  widgetId: string;
  presetId?: string | null;
  props?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  subcomponents?: Component[];
};

type Props = {
  /** Identifies the section; the inspector starts over when it changes. */
  targetKey: string;
  instance: Component;
  presetTitle?: string;
  isBasicUser?: boolean;
  /** Apply new props to the draft (already debounced here). `key` is the field that changed. */
  onApply: (props: Record<string, any>, key: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** A property editor replaced the whole section (e.g. made it a custom widget). */
  onReplace: (component: Component) => void;
  onOpenFull: () => void;
};

// Widgets that can run scripts re-run them when they re-render, so their edits
// apply after a longer pause; listeners then don't pile up while typing.
const QUICK_MS = 250;
const SCRIPTED_MS = 900;
// The inspector stays open after a replace; nothing to continue to or close.
const stay = () => undefined;
const isScripted = (c: Component) => c.widgetId === 'CustomWidget'
  || (c.widgetId === 'AiGeneratedWidget' && Boolean(c.props && c.props.content && c.props.content.js));

/**
 * The selected section's fields in the inspector column. Edits apply to the
 * page as you type (debounced); undo and other outside changes reload it.
 */
const SectionInspector: React.FC<Props> = ({
  targetKey, instance, presetTitle, isBasicUser, onApply, onReplace, onOpenFull
}) => {
  // A pending edit keeps the apply function of the section it was made in, so
  // switching sections can't send it to the new one.
  const pending = useRef<{ props: Record<string, any>; key: string; apply: Props['onApply']; target: string } | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const timer = useRef<number | null>(null);

  // What the draft should hold if nothing else changed it: the props at start, then each applied edit.
  const known = useRef<{ target: string; sig: string; revision: number }>({ target: targetKey, sig: propsSignature(instance.props), revision: 0 });
  const sig = propsSignature(instance.props);
  if (known.current.target !== targetKey) {
    known.current = { target: targetKey, sig, revision: 0 };
  } else if (sig !== known.current.sig && !pending.current) {
    // Undo, redo, a restore or the full editor changed it: start over from the draft.
    known.current = { target: targetKey, sig, revision: known.current.revision + 1 };
  }

  const flush = useCallback(() => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    // (After a switch, this is the previous section's edit: the new one's state stays.)
    if (next.target === known.current.target) known.current = { ...known.current, sig: propsSignature(next.props) };
    next.apply(next.props, next.key);
  }, []);

  // Apply what's pending before switching sections or closing.
  useEffect(() => flush, [targetKey, flush]);

  const onLiveChange = (component: Component, key: string) => {
    pending.current = { props: component.props || {}, key, apply: onApply, target: targetKey };
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, isScripted(instance) ? SCRIPTED_MS : QUICK_MS);
  };

  return (
    // Leaving the inspector (clicking the canvas, undo, another panel) applies what's pending.
    <div onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) flush(); }}>
      <EditComponent
        key={`${targetKey}:${known.current.revision}`}
        variant="panel"
        instance={instance}
        presetTitle={presetTitle}
        isBasicUser={isBasicUser}
        allowReplace
        onLiveChange={onLiveChange}
        onChange={(component: Component) => { flush(); onReplace(component); }}
        onContinue={stay}
        onClose={stay}
        onOpenFull={() => { flush(); onOpenFull(); }}
      />
    </div>
  );
};

export default SectionInspector;
